import { useCallback, useEffect, useState } from "react";
import { ChevronLeft, ChevronRight, X, Download } from "lucide-react";

import type { ArticleZone } from "../api/issues";

/**
 * Full-screen view of ONE article crop, with prev/next through the page's articles.
 *
 * Why this exists: on the ten papers that ship crops, the page we store is 600x936 and
 * genuinely unreadable. The crop (4-6x linear) is the only legible form of the story, so
 * "click the article" is not a convenience, it is how these papers are read at all.
 *
 * The crop is fetched straight from the share's public path, NOT through the API proxy,
 * so no JWT is appended and large images do not pass through uvicorn.
 */
export function ArticleLightbox({
  zones, index, onClose, onIndex, label,
}: {
  zones: ArticleZone[];
  index: number;
  onClose: () => void;
  onIndex: (i: number) => void;
  label: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const z = zones[index];

  const go = useCallback(
    (d: number) => {
      const n = index + d;
      if (n >= 0 && n < zones.length) {
        setLoaded(false);
        onIndex(n);
      }
    },
    [index, zones.length, onIndex],
  );

  // Arrow keys move between articles and Escape closes. Bound on window because the
  // overlay itself is not focusable and we do not want to steal focus from the reader.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { onClose(); return; }
      if (e.key === "ArrowRight") { e.preventDefault(); go(1); }
      if (e.key === "ArrowLeft") { e.preventDefault(); go(-1); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go, onClose]);

  if (!z) return null;

  // Download the crop itself rather than opening it: a plain <a download> on a
  // cross-origin URL is ignored by browsers, so fetch to a blob first. This is the same
  // approach DownloadDialog uses for page bytes.
  const download = async () => {
    try {
      const resp = await fetch(z.crop_url);
      const blob = await resp.blob();
      const href = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = href;
      // The stored filename is already self-describing
      // (mar_lokmat_kolhapur_2026-10-29_p01_a01_middle-center.jpg), so keep it.
      a.download = z.crop_url.split("/").pop() || `article-${z.article_no}.jpg`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(href);
    } catch {
      // Fall back to a new tab if the blob fetch is blocked.
      window.open(z.crop_url, "_blank", "noopener");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex flex-col"
      style={{ background: "rgba(0,0,0,0.88)" }}
      onClick={onClose}
    >
      {/* header */}
      <div
        className="flex items-center justify-between px-4 py-2 font-mono text-[10px] uppercase tracking-folio"
        style={{ color: "rgba(255,255,255,0.75)" }}
        onClick={(e) => e.stopPropagation()}
      >
        <span>
          {label} · article {String(z.article_no).padStart(2, "0")} of {zones.length}
          {z.position ? ` · ${z.position}` : ""}
          {z.crop_w && z.crop_h ? ` · ${z.crop_w}x${z.crop_h}` : ""}
        </span>
        <span className="flex items-center gap-3">
          <button onClick={download} className="hover:text-white flex items-center gap-1" title="Download this article">
            <Download size={13} /> Save
          </button>
          <button onClick={onClose} className="hover:text-white" title="Close (Esc)">
            <X size={15} />
          </button>
        </span>
      </div>

      {z.headline ? (
        <div
          className="px-4 pb-2 text-sm"
          style={{ color: "rgba(255,255,255,0.92)" }}
          onClick={(e) => e.stopPropagation()}
        >
          {z.headline}
        </div>
      ) : null}

      {/* image + arrows */}
      <div className="flex-1 flex items-center justify-between gap-2 px-2 pb-4 min-h-0">
        <button
          onClick={(e) => { e.stopPropagation(); go(-1); }}
          disabled={index === 0}
          className="shrink-0 p-2 disabled:opacity-20"
          style={{ color: "white" }}
          title="Previous article ()"
        >
          <ChevronLeft size={26} />
        </button>

        <div className="flex-1 h-full flex items-center justify-center overflow-auto" onClick={(e) => e.stopPropagation()}>
          {!loaded && (
            <span className="font-mono text-[10px]" style={{ color: "rgba(255,255,255,0.5)" }}>
              loading…
            </span>
          )}
          <img
            src={z.crop_url}
            alt={z.headline || `Article ${z.article_no}`}
            onLoad={() => setLoaded(true)}
            style={{
              maxWidth: "100%",
              maxHeight: "100%",
              objectFit: "contain",
              display: loaded ? "block" : "none",
              background: "white",
            }}
          />
        </div>

        <button
          onClick={(e) => { e.stopPropagation(); go(1); }}
          disabled={index >= zones.length - 1}
          className="shrink-0 p-2 disabled:opacity-20"
          style={{ color: "white" }}
          title="Next article ()"
        >
          <ChevronRight size={26} />
        </button>
      </div>
    </div>
  );
}
